import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Loader2, RefreshCw, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PanelTableCard, PanelTableHeader, PanelTableFrame,
} from "@/components/ui/panel-table";

const statusBadge = (status, isDark) => {
  if (status === "manual") {
    return isDark ? "bg-blue-500/20 text-blue-300 border-blue-500/30" : "bg-blue-50 text-blue-800 border-blue-200";
  }
  if (status === "auto") {
    return isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  return isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-50 text-amber-800 border-amber-200";
};

const statusLabel = (status) => {
  if (status === "manual") return "Manuel";
  if (status === "auto") return "Otomatik";
  return "Eşleşmedi";
};

const MrAssistantMappingPage = ({
  apiBase, isDark, textClass, labelClass, isSuperAdmin, userCompanyId, companies = [],
}) => {
  const spnDefault = useMemo(() => {
    const spn = companies.find((c) => String(c.kod || "").toLowerCase() === "spn");
    return spn ? String(spn.id) : (companies[0] ? String(companies[0].id) : "");
  }, [companies]);

  const [companyId, setCompanyId] = useState(() => (isSuperAdmin ? spnDefault : String(userCompanyId || "")));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [board, setBoard] = useState(null);
  const [draftAgent, setDraftAgent] = useState({});

  useEffect(() => {
    if (isSuperAdmin && spnDefault && !companyId) {
      setCompanyId(spnDefault);
    }
  }, [isSuperAdmin, spnDefault, companyId]);

  const effectiveCompanyId = isSuperAdmin ? (companyId ? Number(companyId) : null) : Number(userCompanyId);

  const loadBoard = useCallback(async (refresh = false) => {
    if (!effectiveCompanyId) {
      setLoading(false);
      setRefreshing(false);
      setBoard(null);
      return;
    }
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/mr-assistant-mapping`, {
        params: { company_id: effectiveCompanyId, refresh: refresh ? 1 : 0 },
        timeout: refresh ? 180000 : 120000,
      });
      if (!res.data?.ok) {
        toast.error(res.data?.message || "Liste alınamadı");
        setBoard(null);
        return;
      }
      setBoard(res.data);
      const drafts = {};
      (res.data.rows || []).forEach((row) => {
        if (row.agent_id) drafts[row.mr_user_id] = String(row.agent_id);
      });
      setDraftAgent(drafts);
      if (refresh) {
        toast.success(`${res.data.mr_user_count ?? 0} asistan (${res.data.source || "firebase"})`);
      } else if (res.data.hint && !(res.data.rows || []).length) {
        toast.info(res.data.hint, { duration: 8000 });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Yüklenemedi";
      toast.error(msg);
      if (err.response?.status === 404) {
        toast.error("API /mr-assistant-mapping sunucuda yok — backend deploy gerekli.", { duration: 10000 });
      }
      setBoard(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBase, effectiveCompanyId]);

  useEffect(() => {
    loadBoard(false);
  }, [loadBoard]);

  const saveRow = async (row) => {
    const agentId = draftAgent[row.mr_user_id];
    if (!agentId) {
      toast.warning("Panel agent seçin");
      return;
    }
    setSavingId(row.mr_user_id);
    try {
      await axios.post(`${apiBase}/mr-assistant-mapping`, {
        company_id: effectiveCompanyId,
        mr_user_id: row.mr_user_id,
        agent_id: Number(agentId),
        display_name: row.firebase_name || row.display_name,
      });
      toast.success("Eşleştirme kaydedildi");
      await loadBoard(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingId(null);
    }
  };

  const clearManual = async (row) => {
    if (row.match_status !== "manual") return;
    if (!window.confirm(`${row.display_name} manuel eşleştirmesi silinsin mi?`)) return;
    setSavingId(row.mr_user_id);
    try {
      await axios.delete(`${apiBase}/mr-assistant-mapping`, {
        data: { company_id: effectiveCompanyId, mr_user_id: row.mr_user_id },
      });
      toast.success("Manuel eşleştirme kaldırıldı");
      await loadBoard(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    } finally {
      setSavingId(null);
    }
  };

  const applyDaily = async () => {
    try {
      const res = await axios.post(`${apiBase}/mr-assistant-mapping`, {
        company_id: effectiveCompanyId,
        action: "apply_daily",
      });
      toast.success(`Günlük MR tablosu güncellendi (${res.data?.daily_rows_updated ?? 0} satır)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uygulanamadı");
    }
  };

  const agents = board?.agents || [];
  const rows = board?.rows || [];
  const inputTone = isDark ? "border-slate-700 bg-slate-900 text-white" : "border-gray-200 bg-white";

  return (
    <PanelTableCard isDark={isDark}>
      <PanelTableHeader
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        accent="cyan"
        icon={Link2}
        title="Hisar MR — Asistan Eşleştirme"
        description="Hisar MR asistanlarını panel agentlarıyla eşleştirin. Liste öncelikle veritabanı ve günlük arşivden gelir (Firebase users listesi kotayı tüketmez)."
        toolbar={(
          <div className="flex flex-wrap gap-2 items-center">
            {isSuperAdmin && (
              <Select value={companyId || undefined} onValueChange={setCompanyId}>
                <SelectTrigger className={`w-[180px] h-9 ${inputTone}`}>
                  <SelectValue placeholder="Firma" />
                </SelectTrigger>
                <SelectContent>
                  {companies.filter((c) => c.status !== 0).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button type="button" size="sm" variant="outline" className={isDark ? "border-slate-700" : ""} disabled={refreshing || loading} onClick={() => loadBoard(true)}>
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Firebase users yenile
            </Button>
            <Button type="button" size="sm" variant="secondary" disabled={!board?.maps?.length} onClick={applyDaily}>
              MR günlük tablosuna uygula
            </Button>
          </div>
        )}
      />

      {!effectiveCompanyId && isSuperAdmin && (
        <p className={`px-4 pb-2 text-sm ${isDark ? "text-amber-300" : "text-amber-800"}`}>
          Lütfen üstten firma seçin (ör. SPN).
        </p>
      )}

      {board?.hint && !(board.rows || []).length && (
        <p className={`px-4 pb-2 text-sm ${isDark ? "text-amber-200" : "text-amber-900"}`}>
          {board.hint}
        </p>
      )}

      {board && (
        <p className={`px-4 pb-2 text-xs ${labelClass}`}>
          Kaynak: {board.source === "firebase" ? "Firebase users" : board.source === "archive" ? "MR günlük arşiv" : "önbellek"}
          {" · "}
          Panel agent: {board.agents?.length ?? 0}
          {" · "}
          <span className={board.unmatched_count > 0 ? (isDark ? "text-amber-400" : "text-amber-700") : ""}>
            Eşleşmeyen: {board.unmatched_count ?? 0}
          </span>
          {" / "}
          Toplam: {board.mr_user_count ?? 0}
        </p>
      )}

      <PanelTableFrame isDark={isDark}>
        {loading ? (
          <div className={`p-8 text-center text-sm ${labelClass}`}>
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Yükleniyor…
          </div>
        ) : !effectiveCompanyId ? (
          <div className={`p-8 text-center text-sm ${labelClass}`}>Firma seçilmedi.</div>
        ) : !rows.length ? (
          <div className={`p-8 text-center text-sm space-y-2 ${labelClass}`}>
            <p>Asistan listesi boş.</p>
            <p className="text-xs">«Firebase&apos;den yenile» — sunucuda <code className="opacity-80">api/secrets/mr-platform.json</code> olmalı.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? "border-b border-slate-800 text-slate-400" : "border-b border-gray-200 text-gray-500"}>
                  <th className="text-left p-3 font-medium">Hisar MR (name)</th>
                  <th className="text-left p-3 font-medium">Durum</th>
                  <th className="text-left p-3 font-medium min-w-[220px]">Panel agent</th>
                  <th className="text-right p-3 font-medium w-[140px]">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.mr_user_id} className={isDark ? "border-b border-slate-800/80" : "border-b border-gray-100"}>
                    <td className="p-3 align-middle">
                      <div className={`font-medium ${textClass}`}>
                        {row.firebase_name || row.display_name}
                      </div>
                      {row.firebase_name && row.display_name && row.firebase_name !== row.display_name ? (
                        <div className={`text-[11px] ${labelClass}`}>Eşleştirme adı: {row.display_name}</div>
                      ) : null}
                      <div className={`text-[10px] font-mono ${labelClass}`}>{row.mr_user_id}</div>
                      {row.code ? <div className={`text-[10px] ${labelClass}`}>Kod: {row.code}</div> : null}
                    </td>
                    <td className="p-3 align-middle">
                      <Badge variant="outline" className={statusBadge(row.match_status, isDark)}>
                        {statusLabel(row.match_status)}
                      </Badge>
                      {row.agent_name && row.match_status !== "unmatched" ? (
                        <div className={`text-[11px] mt-1 ${labelClass}`}>{row.agent_name}</div>
                      ) : null}
                    </td>
                    <td className="p-3 align-middle">
                      <Select
                        value={draftAgent[row.mr_user_id] || ""}
                        onValueChange={(v) => setDraftAgent((d) => ({ ...d, [row.mr_user_id]: v }))}
                      >
                        <SelectTrigger className={`h-9 ${inputTone}`}>
                          <SelectValue placeholder="Agent seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {agents.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.name}
                              {a.mr_mapped ? " · MR" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 align-middle text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" size="sm" variant="outline" className={isDark ? "border-slate-700" : ""} disabled={savingId === row.mr_user_id} onClick={() => saveRow(row)}>
                          {savingId === row.mr_user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        </Button>
                        {row.match_status === "manual" ? (
                          <Button type="button" size="sm" variant="ghost" className="text-rose-500" disabled={savingId === row.mr_user_id} onClick={() => clearManual(row)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelTableFrame>
    </PanelTableCard>
  );
};

export default MrAssistantMappingPage;
